<template>
  <b-col>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
          <div>
            <h2 class="font-weight-bolder m-0">
              Usuarios
            </h2>
          </div>
          <div class="d-flex align-items-center">
            <div class="mr-2">
              <b-link :to="{ name: 'createUser' }">
                <b-button
                  variant="primary"
                  class="text-nowrap"
                >
                  {{ "Añadir nuevo usuario" }}
                </b-button>
              </b-link>
            </div>
            <span
              class="cursor-pointer"
              @click="toggleFilterSidebar"
            >
              <feather-icon
                icon="FilterIcon"
                size="20"
              />
            </span>
          </div>
        </div>
      </b-col>
      <b-col cols="12">
        <!-- Sidebar de filtros -->
        <filter-sidebar
          v-model="showFilterSidebar"
          :has-active-filters="hasActiveFilters"
          :active-filters="computedActiveFilters"
          @apply-filters="handleSearch"
          @clear-filters="clearFilters"
          @remove-filter="removeActiveFilter"
        >
          <!-- Slot de filtros -->
          <template #filters>
            <b-form-group
              label="Búsqueda"
              label-for="search-input"
            >
              <b-input-group class="input-group">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  id="search-input"
                  v-model="searchTerm"
                  type="text"
                  :placeholder="'Búsqueda'"
                  @keydown.enter.native="handleSearch"
                />
              </b-input-group>
            </b-form-group>
            <b-form-group label-for="department-select">
              <label class="d-flex align-items-center">
                Departamento
                <span class="ml-1 align-middle" v-b-tooltip.hover title="Selecciona un departamento para ver los fichajes realizados con QR en ese área.">
                <feather-icon icon="InfoIcon" size="18" class="text-primary" />
              </span>
              </label>
              <BaseSelect
                  v-model="department_id"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :httpBody="{
                client_id: currentClient ? currentClient.id : ''
              }"
                  :resource="'/departments'"
                  :placeholder="'Todos los departamentos'"
                  @input="handleDepartmentChange"
              />
            </b-form-group>
          </template>
        </filter-sidebar>
      </b-col>
    </b-row>
    <b-card no-body>
      <b-table
        hover
        responsive
        :items="items"
        :fields="columns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @sort-changed="handleSortChanged"
      >
        <template #cell(fullName)="data">
          <div class="d-flex align-items-center">
            <b-avatar
              variant="dark"
              size="md"
              :src="data.item.avatar"
            />
            <span class="ml-1">
              <b-link
                :to="{ name: 'viewUser', params: { id: data.item.id } }"
                class="link"
              >
                {{ data.item.name }}
              </b-link>
            </span>
          </div>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.active)">
            {{ statusVariantText(data.item.active) }}
          </b-badge>
        </template>
        <template #cell(departments)="data">
          <div v-if="data.item.departments">
            <span
              v-for="(item, index) in data.item.departments"
              :key="item.id"
            >
              <span v-if="index !== 0">, </span>{{ item.name }}</span>
          </div>
        </template>
        <template #cell(roles)="data">
          <div v-if="data.item.roles">
            <span
              v-for="item in data.item.roles"
              :key="item.id"
              class="mr-50"
            >
              {{ item.display_name }}
            </span>
          </div>
        </template>
        <template #cell(actions)="data">
          <span>
            <b-link
              :to="{ name: 'viewUser', params: { id: data.item.id } }"
              class="d-inline-block px-50 text-dark"
            >
              <feather-icon icon="EyeIcon" />
            </b-link>
            <b-link
              :to="{ name: 'editUser', params: { id: data.item.id } }"
              class="d-inline-block px-50 text-dark"
            >
              <feather-icon icon="Edit2Icon" />
            </b-link>
            <span
              class="d-inline-block px-50 text-danger"
              @click="deleteUser(data.item.id, data.item.name)"
            >
              <feather-icon icon="TrashIcon" />
            </span>
          </span>
        </template>
      </b-table>
      <div class="d-flex align-items-center justify-content-between mb-1 px-2">
        <div class="d-flex align-items-center mb-0">
          <span class="text-nowrap"> {{ "Por página" }}: </span>
          <b-form-select
            v-model="pageLength"
            :options="pages"
            class="ml-50 mr-1"
            @input="handlePageChange"
          />
          <span class="text-nowrap"> {{ "Total" }}: {{ totalItems }}</span>
        </div>
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalItems"
            :per-page="pageLength"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @change="handleChangePage"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BBadge,
  BPagination,
  BFormInput,
  BFormSelect,
  BCard,
  BTable,
  BButton,
  BAvatar,
  BCol,
  BRow,
  BLink,
  BInputGroup,
  BInputGroupPrepend,
  BFormGroup, VBTooltip,
} from 'bootstrap-vue'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import FilterSidebar from '@/components/ui/FilterSidebar.vue'
import BaseSelect from '@/components/ui/select/BaseSelect.vue'

export default {
  components: {
    BaseSelect,
    BAvatar,
    BBadge,
    BCard,
    BPagination,
    BFormInput,
    BFormSelect,
    BTable,
    BButton,
    BCol,
    BRow,
    BLink,
    BInputGroup,
    BInputGroupPrepend,
    BFormGroup,
    FilterSidebar,
    VBTooltip,
  },
  data() {
    return {
      currentPage: 1,
      pageLength: 25,
      pages: ['25', '50', '100'],
      columns: [
        {
          label: 'Nombre',
          key: 'fullName',
          sortable: true,
        },
        {
          label: 'Usuario',
          key: 'username',
          sortable: true,
        },
        {
          label: 'Estado',
          key: 'status',
          sortable: true,
        },
        {
          label: 'Departamentos',
          key: 'departments',
          sortable: true,
        },
        {
          label: 'Rol',
          key: 'roles',
          sortable: true,
        },
        {
          label: 'Acciones',
          key: 'actions',
          class: 'column-actions',
        },
      ],
      searchTerm: '',
      appliedSearchTerm: '',
      appliedDepartmentId: null,
      sortBy: '',
      sortDesc: false,
      showFilterSidebar: false,
      department_id: '',
    }
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  computed: {
    ...mapGetters({
      items: 'users/getItems',
      totalItems: 'users/getTotalItems',
      currentClient: 'clients/getCurrentClient',
    }),
    statusVariant() {
      const statusColor = {
        1: 'success',
        0: 'danger',
      }
      return status => statusColor[status]
    },
    statusVariantText() {
      const statusText = {
        1: 'Activo',
        0: 'Desactivado',
      }
      return status => statusText[status]
    },
    hasActiveFilters() {
      return this.appliedSearchTerm !== '' || this.appliedDepartmentId !== null
    },
    computedActiveFilters() {
      const filters = []
      if (this.appliedSearchTerm) {
        filters.push({
          label: 'Búsqueda',
          value: this.appliedSearchTerm,
          icon: 'SearchIcon',
          filterType: 'search',
        })
      }
      if (this.appliedDepartmentId) {
        filters.push({
          label: 'Departamento',
          value: this.appliedDepartmentId.name,
          icon: 'LayersIcon',
          filterType: 'department',
        })
      }
      return filters
    },
  },
  created() {
    this.chargeData()
  },
  methods: {
    ...mapActions({
      list: 'users/getListUsers',
      delete: 'users/delete',
    }),
    chargeData() {
      // Mapeo de columnas para orderBy
      let orderBy = ''
      if (this.sortBy) {
        const type = this.sortDesc ? 'desc' : 'asc'
        const columnMap = {
          fullName: 'name',
          username: 'username',
          status: 'active',
          departments: 'departments',
          roles: 'roles',
        }
        const column = columnMap[this.sortBy] || this.sortBy
        orderBy = {
          column,
          type,
        }
      }
      this.list({
        page: this.currentPage,
        per_page: this.pageLength,
        search: this.searchTerm,
        roles: ['super_admin', 'admin', 'operario'],
        active: null,
        client_id: this.currentClient.id,
        orderBy,
        department_id: this.department_id?.id ?? '',
      })
    },
    handleSearch() {
      this.currentPage = 1
      this.appliedSearchTerm = this.searchTerm
      this.appliedDepartmentId = this.department_id
      this.chargeData()
    },
    clearFilters() {
      this.searchTerm = ''
      this.department_id = null
      this.appliedSearchTerm = ''
      this.appliedDepartmentId = null
      this.currentPage = 1
      this.chargeData()
    },
    removeActiveFilter(idx) {
      const f = this.computedActiveFilters[idx]
      if (f && f.filterType === 'search') {
        this.appliedSearchTerm = ''
        this.searchTerm = ''
      } else if (f && f.filterType === 'department') {
        this.appliedDepartmentId = null
        this.department_id = null
      }
      this.currentPage = 1
      this.chargeData()
    },
    toggleFilterSidebar() {
      this.showFilterSidebar = !this.showFilterSidebar
    },
    handleChangePage(page) {
      this.currentPage = page
      this.chargeData()
    },
    handlePageChange() {
      this.currentPage = 1
      this.chargeData()
    },
    handleSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.chargeData()
    },
    deleteUser(id, name) {
      this.$bvModal
        .msgBoxConfirm(`¿Estás seguro de eliminar ${name}?`, {
          bodyClass: 'bodyDelete',
          footerClass: 'footerDelete',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-danger',
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.delete(id)
          }
        })
    },
    handleDepartmentChange() {
      this.currentPage = 1
    },
  },
}
</script>
